/**
 * Support ticket API contract types. Shared between apps/api and apps/web.
 *
 * Browser-diagnostic and snapshot types here are the wire shape — the API stores
 * `SupportTicketBrowserInfo` directly as a jsonb column and accepts it on create.
 */

export type ConsoleLogLevel = "log" | "info" | "warn" | "error" | "debug";

export type ConsoleLogEntry = {
  level: ConsoleLogLevel;
  timestamp: string;
  message: string;
};

export type NetworkLogEntry = {
  method: string;
  url: string;
  status?: number;
  durationMs?: number;
  timestamp: string;
  error?: string;
};

export type SupportTicketBrowserInfo = {
  userAgent: string;
  url: string;
  viewport: { width: number; height: number };
  language: string;
  capturedAt: string;
  console: ConsoleLogEntry[];
  network: NetworkLogEntry[];
};

export const SUPPORT_ATTACHMENT_TYPES = ["flow_instance", "chat_thread"] as const;
export type SupportAttachmentType = (typeof SUPPORT_ATTACHMENT_TYPES)[number];

export type SupportTicketAttachmentSnapshot = {
  displayName: string;
  entityType: SupportAttachmentType;
  data: Record<string, unknown>;
};

export const SUPPORT_TICKET_STATUSES = ["open", "closed"] as const;
export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

export type SupportTicketUser = {
  id: string;
  name: string;
  email: string;
  image: string | null;
  language: string | null;
};

/** Lightweight tenant info attached to tickets in the Quantfolio cross-tenant view. */
export type SupportTicketTenant = {
  id: string;
  slug: string;
  name: string;
  defaultLanguage: string;
};

export type SupportTicket = {
  id: string;
  title: string;
  description: string;
  imageUrl: string | null;
  status: SupportTicketStatus;
  commentCount: number;
  attachmentCount: number;
  createdById: string;
  createdBy: SupportTicketUser;
  tenantId: string;
  tenant: SupportTicketTenant | null;
  createdAt: string;
  updatedAt: string;
};

export type SupportTicketComment = {
  id: string;
  ticketId: string;
  content: string;
  /**
   * Id of the comment this mirrors in a source system, namespaced by it
   * (`notion:<comment-id>`). Null for comments written in QP. Unique per
   * ticket — see the admin-api POST /support-tickets/{id}/comments.
   */
  externalSyncId: string | null;
  createdById: string;
  createdBy: SupportTicketUser;
  createdAt: string;
  updatedAt: string;
};

export type SupportTicketAttachment = {
  id: string;
  ticketId: string;
  entityType: SupportAttachmentType;
  entityId: string;
  snapshot: SupportTicketAttachmentSnapshot;
  createdById: string;
  createdAt: string;
};

export type SupportTicketDetail = {
  ticket: SupportTicket & { browserInfo: SupportTicketBrowserInfo | null };
  comments: SupportTicketComment[];
  attachments: SupportTicketAttachment[];
};

export type CreateSupportTicketRequest = {
  title: string;
  description: string;
  imageUrl?: string | null;
  browserInfo?: SupportTicketBrowserInfo | null;
  /**
   * Attachments submitted with the ticket. Sent here rather than POSTed
   * afterwards so the ticket is never readable in a half-assembled state — see
   * `createTicket`. A bad entity fails the whole submission.
   */
  attachments?: { entityType: SupportAttachmentType; entityId: string }[];
};

export type UpdateSupportTicketRequest = {
  title?: string;
  description?: string;
  status?: SupportTicketStatus;
};

/** Sidebar badge: how many visible tickets have a comment this user has not read. */
export type SupportTicketUnreadCountResponse = {
  count: number;
};

/**
 * Marking a ticket read. `readThroughCommentId` is the newest comment the
 * caller actually rendered: the marker is set to that comment's timestamp, so a
 * comment arriving between loading the detail and recording the read is not
 * swallowed. Omit it to mark read as of now.
 */
export type MarkSupportTicketReadRequest = {
  readThroughCommentId?: string;
};
